/**
 * Lightweight in-flight tab registry (no global write queue).
 *
 * Tracks which tabs are mid-query (thinking) or mid-apply/deck so persisted
 * loading state is not demoted to "Interrupted" on file switch/reload.
 */

export type InFlightOperationKind = "chat" | "deck";

interface InFlightOperation {
  documentId: string;
  tabId: string;
  kind: InFlightOperationKind;
}

const thinkingTickets = new Map<symbol, { documentId: string; tabId: string }>();
const activeOperations = new Map<string, { token: symbol; operation: InFlightOperation }>();

function operationKey(documentId: string, tabId: string): string {
  return `${documentId}::${tabId}`;
}

export function registerThinkingPhase(documentId: string, tabId: string): symbol {
  const ticket = Symbol("thinking-phase");
  thinkingTickets.set(ticket, { documentId, tabId });
  return ticket;
}

export function unregisterThinkingPhase(ticket: symbol): void {
  thinkingTickets.delete(ticket);
}

export function retargetThinkingPhaseTicket(ticket: symbol, documentId: string): void {
  const existing = thinkingTickets.get(ticket);
  if (!existing) return;
  thinkingTickets.set(ticket, { documentId, tabId: existing.tabId });
}

export function isThinkingPhaseLive(documentId: string, tabId: string): boolean {
  let isLive = false;
  thinkingTickets.forEach((ticket) => {
    if (ticket.documentId === documentId && ticket.tabId === tabId) {
      isLive = true;
    }
  });
  if (isLive) return true;
  return false;
}

export function registerTabOperation(
  documentId: string,
  tabId: string,
  kind: InFlightOperationKind
): () => void {
  const key = operationKey(documentId, tabId);
  const token = Symbol("tab-operation");
  activeOperations.set(key, {
    token,
    operation: { documentId, tabId, kind },
  });
  return () => {
    const current = activeOperations.get(key);
    if (current?.token === token) {
      activeOperations.delete(key);
    }
  };
}

export function isTabOperationLive(documentId: string, tabId: string): boolean {
  return activeOperations.has(operationKey(documentId, tabId));
}

export function getActiveOperationForDocument(
  documentId: string
): InFlightOperation | null {
  let activeOperation: InFlightOperation | null = null;
  activeOperations.forEach((entry) => {
    if (activeOperation) return;
    const operation = entry.operation;
    if (operation.documentId === documentId) {
      activeOperation = operation;
    }
  });
  if (activeOperation) return activeOperation;
  return null;
}

export function isTabFlowLive(documentId: string, tabId: string): boolean {
  return isThinkingPhaseLive(documentId, tabId) || isTabOperationLive(documentId, tabId);
}

/**
 * Reports whether any PowerPoint query or host mutation is currently active.
 *
 * @returns `true` for thinking, tab-apply, or in-deck replacement work.
 * @throws Never.
 */
export function hasAnyInFlightPowerPointWork(): boolean {
  return (
    thinkingTickets.size > 0 ||
    activeOperations.size > 0 ||
    inDeckSlideReplacementCounts.size > 0
  );
}

// In-deck slide-replacement tracking (PresLink / full-deck refresh).
//
// A PresLink refresh inserts the processed slides, verifies the count, then
// deletes the originals. During that window PowerPoint transiently reports a
// replacement slide as "selected". The active-slide poll in `useTabs` would
// otherwise treat that as manual navigation and open/select an empty tab over
// the in-flight chat. Callers mark the window with `beginInDeckSlideReplacement`
// so `useTabs` can suppress that transient adoption ONLY while a replacement is
// actually running — ordinary cross-slide navigation during a normal chat turn
// stays unaffected. Keyed by FactSet `openPresentationId` and ref-counted so
// overlapping replacements on the same deck don't clear the flag early.
const inDeckSlideReplacementCounts = new Map<string, number>();

function deckReplacementKey(presentationId: string | null | undefined): string | null {
  const normalized = (presentationId ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}

export function beginInDeckSlideReplacement(
  presentationId: string | null | undefined
): () => void {
  const key = deckReplacementKey(presentationId);
  if (!key) {
    throw new Error("A concrete openPresentationId is required to track slide replacement.");
  }
  inDeckSlideReplacementCounts.set(key, (inDeckSlideReplacementCounts.get(key) ?? 0) + 1);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const remaining = (inDeckSlideReplacementCounts.get(key) ?? 1) - 1;
    if (remaining > 0) {
      inDeckSlideReplacementCounts.set(key, remaining);
    } else {
      inDeckSlideReplacementCounts.delete(key);
    }
  };
}

export function isInDeckSlideReplacementInProgress(
  presentationId: string | null | undefined
): boolean {
  const key = deckReplacementKey(presentationId);
  return key ? inDeckSlideReplacementCounts.has(key) : false;
}
